/**
 * Contacts API — the "who can I chat with" directory: users in your org (tenant)
 * who have OPTED IN to chat/presence. Sourced from the **SDK server**
 * (`/api/v1/contacts/directory`), NOT the inMeet backend. `id` = contactPresenceId.
 */
import type { Contact } from '@/features/meetings/data/contacts'
import { config } from '@/config/env'
import { getSdkOrAppToken } from '@/lib/sdk-session-token'

// SDK server base (falls back to the app API when VITE_SDK_API_URL is unset).
const SDK = import.meta.env.VITE_SDK_API_URL || config.apiUrl

function getToken(): string {
  try {
    const raw = localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (raw) {
      const parsed = JSON.parse(raw) as any
      const session = parsed?.session
      if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
      return session?.accessToken || ''
    }
  } catch {}
  return ''
}

export type ContactsParams = { q?: string; limit?: number; offset?: number; scope?: 'sub' | 'scope' }
export type ContactsResponse = { contacts: Contact[]; total: number; limit: number; offset: number }

/** Reach for a contact search. */
export type SearchScope = 'subId' | 'scopeId' | 'global'
/** A contact returned by search — company name disambiguates the same person
 *  across companies. `id` = contactPresenceId (start a DM with it). */
export type SearchResultContact = {
  id: string
  name: string | null
  email: string
  avatarUrl: string | null
  contactOrgName: string
  subId: string | null
}
// `offset`/`hasMore` are only sent by a server that knows about paging; an
// older server's response simply omits them, and callers must treat a missing
// `hasMore` as false (there is no second page to ask for).
export type SearchResponse = {
  contacts: SearchResultContact[]
  scope: SearchScope
  total: number
  offset?: number
  hasMore?: boolean
}

export const contactsApi = {
  async list(params: ContactsParams = {}): Promise<ContactsResponse> {
    const token = await getSdkOrAppToken()
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.scope === 'scope') qs.set('scope', 'scope')
    if (params.limit  !== undefined) qs.set('limit',  String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    const res = await fetch(`${SDK}/api/v1/contacts/directory${query}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error?.message || `Failed to load contacts (${res.status})`)
    }
    return res.json()
  },

  /**
   * Unified contact discovery. **Partial** match on name/email (`q` empty =
   * browse everyone in reach). `scope` = 'subId' (your sub-org, default) |
   * 'scopeId' (your whole scope) | 'global' (whole platform). `limit` default 50.
   * `offset` pages through a scope's results (alphabetical, stable order);
   * omitted when 0 so a plain first-page call looks the same as before this
   * param existed. Returns each match + `contactOrgName`/`avatarUrl`. DM with `id`.
   */
  async search(q = '', scope: SearchScope = 'subId', limit = 50, offset = 0): Promise<SearchResponse> {
    const token = await getSdkOrAppToken()
    const qs = new URLSearchParams({ scope, limit: String(limit) })
    if (q) qs.set('q', q)
    if (offset > 0) qs.set('offset', String(offset))
    const res = await fetch(`${SDK}/api/v1/contacts/search?${qs.toString()}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body?.error?.message || `Contact search failed (${res.status})`)
    }
    return res.json()
  },

  /**
   * Exact lookup by id (a `contactPresenceId`), any scope, no privacy filter.
   * For resolving ids already in hand (a conversation's participants) rather
   * than searching by name, which can miss or mismatch a stale copy of a
   * display name. Throws on a non-ok response (including a 404 from a server
   * that doesn't have this route yet) so callers can fall back to search.
   */
  async lookup(ids: string[]): Promise<SearchResultContact[]> {
    const token = await getSdkOrAppToken()
    const qs = new URLSearchParams({ ids: ids.join(',') })
    const res = await fetch(`${SDK}/api/v1/contacts/lookup?${qs.toString()}`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      // The status always ends the message: callers tell "no such route on
      // this server yet" (404/405) from a passing failure by it.
      throw new Error(`${body?.error?.message || 'Contact lookup failed'} (${res.status})`)
    }
    const data = (await res.json()) as { contacts: SearchResultContact[] }
    return data.contacts
  },
}
